// ================================================================
// ↩️ REFUNDS REPORT - Relatório de Estornos
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors';
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const RefundsReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refundsData, setRefundsData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartsGridColor = useChartsColors();
    const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: {
        grid: {
          color: chartsGridColor
        }
      },
      y: {
        grid: {
          color: chartsGridColor
        },
      }
    }
  };

  const loadRefundsReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('↩️ Carregando Relatório de Estornos com filtros...', filtersToUse);

      const result = await ReportsService.getRefundsReport({
        period: filtersToUse.period,
        product_id: filtersToUse.advancedFilters?.product_id
      });

      if (result.success) {
        setRefundsData(result.data);
        console.log('✅ Relatório de Estornos carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRefundsReport = useCallback(async () => {
    loadRefundsReportWithFilters(filters);
  }, [filters, loadRefundsReportWithFilters]);

  useEffect(() => {
    loadRefundsReport();
  }, [loadRefundsReport]);

  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => {
      const newFilters = { ...prev, period: newPeriod };
      setTimeout(() => loadRefundsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev, advancedFilters };
      setTimeout(() => loadRefundsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const refundTrendData = refundsData?.charts?.refundTrend ? {
    labels: refundsData.charts.refundTrend.map(item => new Date(item.date).toLocaleDateString('pt-BR')),
    datasets: [{
      label: 'Estornados',
      data: refundsData.charts.refundTrend.map(item => item.refunded_count),
      borderColor: '#dc3545',
      backgroundColor: 'rgba(220, 53, 69, 0.1)',
      tension: 0.4
    }, {
      label: 'Completados',
      data: refundsData.charts.refundTrend.map(item => item.completed_count),
      borderColor: '#28a745',
      backgroundColor: 'rgba(40, 167, 69, 0.1)',
      tension: 0.4
    }]
  } : { labels: [], datasets: [] };

  const refundsByProductData = refundsData?.charts?.refundsByProduct ? {
    labels: refundsData.charts.refundsByProduct.map(item => item.product_name?.substring(0, 20) + '...'),
    datasets: [{
      label: 'Taxa de Estorno (%)',
      data: refundsData.charts.refundsByProduct.map(item => item.refund_rate),
      backgroundColor: '#dc3545',
      borderWidth: 0
    }]
  } : { labels: [], datasets: [] };

  const refundsColumns = refundsData?.charts?.refundsByProduct ? [
    {
      key: 'product_name',
      label: 'Produto',
      sortable: true
    },
    {
      key: 'refunded_count',
      label: 'Estornados',
      sortable: true
    },
    {
      key: 'completed_count',
      label: 'Completados',
      sortable: true
    },
    {
      key: 'refund_rate',
      label: 'Taxa (%)',
      sortable: true,
      render: (value) => `${parseFloat(value).toFixed(2)}%`
    },
    {
      key: 'refunded_value_formatted',
      label: 'Valor Estornado',
      sortable: true
    }
  ] : [];

  return (
    <MasterLayout>
      <Container fluid className="reports-container" style={{ padding: '24px', maxWidth: '100%' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon icon="mdi:undo-variant" style={{ fontSize: '22px', color: '#dc3545' }} />
                <span className='text-contrast' style={{ fontSize: '22px', fontWeight: '700', display: 'block' }}>
                  Relatório de Estornos
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowFilterDrawer(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:filter-variant" style={{ fontSize: '20px' }} />
                  Filtros Avançados
                </Button>
                <Button
                  variant="outline-success"
                  onClick={() => console.log('Exportar CSV')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:file-excel" style={{ fontSize: '20px' }} />
                  Exportar CSV
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={loadRefundsReport}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                >
                  <Icon icon="mdi:refresh" style={{ fontSize: '20px' }} />
                  Atualizar
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filter Bar */}
        <FilterBar
          period={filters.period}
          onPeriodChange={handlePeriodChange}
          onApplyAdvancedFilters={handleAdvancedFilters}
          onRefresh={loadRefundsReport}
          loading={loading}
          advancedFilters={filters.advancedFilters}
          onUserFilterChange={() => {}}
        />

        <hr style={{ margin: '24px 0', borderTop: '2px solid #dee2e6' }} />

        {/* Error State */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6c757d' }}>Carregando dados de estornos...</p>
          </div>
        )}

        {/* Main Content */}
        {!loading && refundsData && (
          <>
            {/* KPIs */}
            <Row className="mb-4">
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={refundsData.kpis.refund_rate.label}
                  value={refundsData.kpis.refund_rate.formatted}
                  icon="mdi:trending-down"
                  color="#dc3545"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={refundsData.kpis.refunded_sales.label}
                  value={refundsData.kpis.refunded_sales.value}
                  icon="mdi:undo-variant"
                  color="#ffc107"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={refundsData.kpis.refunded_value.label}
                  value={refundsData.kpis.refunded_value.formatted}
                  icon="mdi:currency-usd"
                  color="#17a2b8"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={refundsData.kpis.refunded_commissions.label}
                  value={refundsData.kpis.refunded_commissions.formatted}
                  icon="mdi:account-cash"
                  color="#6f42c1"
                />
              </Col>
            </Row>

            {/* Charts */}
            <Row className="mb-4">
              <Col md={8} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Tendência de Estornos
                    </span>
                    <Line data={refundTrendData} options={chartOptions} />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Top 10 Produtos
                    </span>
                    <Bar data={refundsByProductData} options={{ ...chartOptions, indexAxis: 'y' }} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Refunds Table */}
            <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                  Estornos por Produto
                </span>
                <SmartTable
                  columns={refundsColumns}
                  data={refundsData.charts.refundsByProduct || []}
                  searchable={true}
                  pagination={true}
                  itemsPerPage={10}
                />
              </Card.Body>
            </Card>
          </>
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          show={showFilterDrawer}
          onHide={() => setShowFilterDrawer(false)}
          onApply={(filters) => {
            setShowFilterDrawer(false);
            handleAdvancedFilters(filters);
          }}
          currentFilters={filters.advancedFilters}
        />
      </Container>
    </MasterLayout>
  );
};

export default RefundsReport;

